import React, { createContext, useState, useContext, useCallback } from 'react';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';

const NotificationContext = createContext();

export const useNotification = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = useCallback((message, type = 'info') => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);

    // Auto dismiss after 3 seconds
    setTimeout(() => {
      removeNotification(id);
    }, 3000);
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  }, []);

  const styles = {
    container: {
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
    },
    toast: (type) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      minWidth: '300px',
      padding: '16px',
      borderRadius: '8px',
      backgroundColor: '#fff',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      borderLeft: `4px solid ${
        type === 'success' ? '#10b981' : 
        type === 'error' ? '#ef4444' : 
        '#3b82f6'
      }`,
      animation: 'slideIn 0.3s ease-out forwards',
      transition: 'all 0.3s ease'
    }),
    message: {
      flex: 1,
      fontSize: '14px',
      color: '#374151',
      fontWeight: '500',
    },
    closeBtn: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#9ca3af',
      padding: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    icon: (type) => ({
      color: type === 'success' ? '#10b981' : type === 'error' ? '#ef4444' : '#3b82f6'
    })
  };

  return (
    <NotificationContext.Provider value={{ addNotification }}>
      {children}
      <div style={styles.container}>
        {notifications.map(notif => (
          <div key={notif.id} style={styles.toast(notif.type)}>
            <div style={styles.icon(notif.type)}>
              {notif.type === 'success' && <CheckCircle size={20} />}
              {notif.type === 'error' && <AlertCircle size={20} />}
              {notif.type === 'info' && <Info size={20} />}
            </div>
            <div style={styles.message}>{notif.message}</div>
            <button style={styles.closeBtn} onClick={() => removeNotification(notif.id)}>
              <X size={16} />
            </button>
          </div>
        ))}
      </div>
      <style>
        {`
          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        `}
      </style>
    </NotificationContext.Provider>
  );
};
